import ConfirmationModal, { DeletionTarget, Disclaimer } from '@components/ConfirmationModal'
import { useDroppable } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { PutEventType } from '@orbit/api-types'
import React, { useRef, useState } from 'react'
import EventDetailsModal from '../EventDetailsModal'
import { DraggingPlaceholder } from './Card.styles'
import CardContent from './CardContent'
import { CardProps } from './types'

const Card: React.FC<CardProps> = ({ event, onUpdate, onDelete }) => {
	const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const cardRef = useRef<HTMLDivElement>(null)
	const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null)

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: `event-${event.id}`,
	})

	const { setNodeRef: setDroppableRef, isOver } = useDroppable({
		id: `card-${event.id}`,
	})

	if (isDragging) {
		return (
			<DraggingPlaceholder
				ref={setNodeRef}
				$transform={CSS.Transform.toString(transform)}
				$transition={transition}
				{...attributes}
				{...listeners}
			/>
		)
	}

	const handleMouseDown = (e: React.MouseEvent) => {
		mouseDownPosRef.current = { x: e.clientX, y: e.clientY }
	}

	const handleCardClick = () => {
		if (mouseDownPosRef.current) {
			mouseDownPosRef.current = null
		}
		setIsDetailsModalOpen(true)
	}

	const handleActualClick = (e: React.MouseEvent) => {
		if (mouseDownPosRef.current) {
			const deltaX = Math.abs(e.clientX - mouseDownPosRef.current.x)
			const deltaY = Math.abs(e.clientY - mouseDownPosRef.current.y)
			const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

			if (distance < 5) {
				handleCardClick()
			}
			mouseDownPosRef.current = null
		}
	}

	const handlePartialUpdate = (updates: PutEventType) => {
		onUpdate(event.id.toString(), updates)
	}

	const handleDeleteClick = () => {
		setIsDeleteModalOpen(true)
	}

	const handleDeleteConfirm = () => {
		onDelete(event.id.toString())
		setIsDeleteModalOpen(false)
		setIsDetailsModalOpen(false)
	}

	const handleDeleteCancel = () => {
		setIsDeleteModalOpen(false)
	}

	const combinedRef = (node: HTMLDivElement | null) => {
		setNodeRef(node)
		setDroppableRef(node)
		if (cardRef.current !== node) {
			(cardRef as React.MutableRefObject<HTMLDivElement | null>).current = node
		}
	}

	return (
		<>
			<CardContent
				event={event}
				isDragging={isDragging}
				isOver={isOver}
				onClick={handleActualClick}
				onMouseDown={handleMouseDown}
				containerRef={combinedRef}
				containerProps={{ ...attributes, ...listeners }}
			/>

			<EventDetailsModal
				isOpen={isDetailsModalOpen}
				onClose={() => setIsDetailsModalOpen(false)}
				onUpdate={handlePartialUpdate}
				onDelete={handleDeleteClick}
				event={event}
			/>

			<ConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={handleDeleteCancel}
				onConfirm={handleDeleteConfirm}
				title="Delete Event"
				message={
					<>
						Are you sure you want to delete '<DeletionTarget>{event.name}</DeletionTarget>'?
						<Disclaimer>This action cannot be undone</Disclaimer>
					</>
				}
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>
		</>
	)
}

export default Card
